import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  TabList,
  Tab,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Text
} from '@fluentui/react-components';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useEmail } from './hooks/useEmail';
import SummaryPanel from './components/SummaryPanel';
import LinksPanel from './components/LinksPanel';
import TasksPanel from './components/TasksPanel';
import SettingsPanel from './components/SettingsPanel';
import { EmailData } from '@shared/types/email';
import { APP_VERSION } from '@shared/utils/constants';
import { addinLogger } from './utils/addinLogger';
import { apiService } from './services/apiService';
import { getAccessToken, getUserIdFromToken } from './services/authService';
import type { UserPreferences } from '@shared/types/user';
import { DEFAULT_FEATURE_TOGGLES, FEATURE_NAMES } from '@shared/utils/constants';

/** Tab value to feature toggle key (for visibility). Settings is always shown. Automation tab is shown when either actions or automation is enabled. */
const TAB_FEATURE: Record<string, keyof UserPreferences['featureToggles'] | 'settings'> = {
  summary: FEATURE_NAMES.SUMMARIES,
  links: FEATURE_NAMES.LINKS,
  automation: FEATURE_NAMES.AUTOMATION,
  settings: 'settings',
};

const App: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<string>('summary');
  const [email, setEmail] = useState<EmailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const { getCurrentEmail } = useEmail();
  const loadEmailRef = useRef<(() => Promise<void>) | null>(null);

  const loadPreferences = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const user = getUserIdFromToken(token);
      const userId = user?.oid ?? 'current-user-id';
      const prefs = await apiService.getUserPreferences(userId);
      setPreferences(prefs);
      return prefs;
    } catch {
      setPreferences(null);
      return null;
    }
  }, []);

  useEffect(() => {
    loadEmail();
  }, []);

  useEffect(() => {
    if (typeof Office === 'undefined' || !Office.context?.mailbox?.addHandlerAsync) return;
    const handler = () => {
      addinLogger.info('App: ItemChanged fired');
      if (Office.context.mailbox.item == null) {
        setEmail(null);
        setLoading(false);
        setError('No item selected');
        return;
      }
      loadEmailRef.current?.();
    };
    (Office.context.mailbox as { addHandlerAsync: (event: Office.EventType, handler: () => void, callback?: (result: { status: number; error?: { message: string } }) => void) => void })
      .addHandlerAsync(Office.EventType.ItemChanged, handler, (result) => {
        if (result?.status !== 0 && result?.error) {
          addinLogger.debug('App: ItemChanged handler registration failed', result.error.message ?? 'unknown');
        }
      });
    return () => {
      if (Office.context?.mailbox && typeof (Office.context.mailbox as { removeHandlerAsync?: (event: Office.EventType, options: { handler: () => void }, callback?: (result: { status: number }) => void) => void }).removeHandlerAsync === 'function') {
        (Office.context.mailbox as { removeHandlerAsync: (event: Office.EventType, options: { handler: () => void }, callback?: (result: { status: number }) => void) => void })
          .removeHandlerAsync(Office.EventType.ItemChanged, { handler }, () => {});
      }
    };
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    if (!preferences?.featureToggles) return;
    const toggles = preferences.featureToggles;
    const visible = (Object.keys(TAB_FEATURE) as Array<keyof typeof TAB_FEATURE>).filter(
      (tab) =>
        tab === 'settings' ||
        (tab === 'automation' ? (toggles.actions || toggles.automation) : toggles[TAB_FEATURE[tab] as keyof typeof toggles])
    );
    if (visible.length && !visible.includes(selectedTab)) {
      setSelectedTab(visible[0]);
    }
  }, [preferences, selectedTab]);

  // Apply task pane width when preferences load (when host supports TaskPaneAPI 1.1)
  useEffect(() => {
    const width = preferences?.taskPaneConfig?.width;
    if (width == null || typeof Office === 'undefined') return;
    try {
      const taskpane = (Office as unknown as { extensionLifeCycle?: { taskpane?: { setWidth: (w: number) => void } } }).extensionLifeCycle?.taskpane;
      if (typeof taskpane?.setWidth === 'function') {
        taskpane.setWidth(width);
      }
    } catch {
      // setWidth not supported in this host (e.g. Outlook desktop)
    }
  }, [preferences?.taskPaneConfig?.width]);

  const loadEmail = async () => {
    addinLogger.info('App.loadEmail: starting');
    try {
      setLoading(true);
      setError(null);
      const emailData = await getCurrentEmail();
      addinLogger.info('App.loadEmail: got email, rendering tabs');
      setEmail(emailData);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load email';
      addinLogger.error('App.loadEmail: failed', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  loadEmailRef.current = loadEmail;

  const handlePreferencesSaved = useCallback(() => {
    loadPreferences().then((prefs) => {
      if (!prefs?.featureToggles) return;
      const toggles = prefs.featureToggles;
      const enabledTabs = (Object.keys(TAB_FEATURE) as Array<keyof typeof TAB_FEATURE>).filter(
        (tab) =>
          tab === 'settings' ||
          (tab === 'automation' ? (toggles.actions || toggles.automation) : toggles[TAB_FEATURE[tab] as keyof typeof toggles])
      );
      if (!enabledTabs.includes(selectedTab)) {
        setSelectedTab(enabledTabs[0] ?? 'settings');
      }
    });
  }, [selectedTab, loadPreferences]);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Spinner label="Loading email..." />
        <Text size={200} style={{ color: '#666' }}>v{APP_VERSION}</Text>
      </div>
    );
  }

  if (error) {
    const isAuthError =
      /sign-in|sign in|token|not available|SSO|ENTRA/i.test(error);
    return (
      <div style={{ padding: '20px' }}>
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
        <Button onClick={loadEmail} style={{ marginTop: '10px' }}>
          Retry
        </Button>
        {isAuthError && (
          <Text size={200} style={{ color: '#666', display: 'block', marginTop: 8 }}>
            Use Outlook with a work or school account (not Outlook.com or Gmail). See docs/ENTRA_SSO_SETUP.md.
          </Text>
        )}
        <Text size={200} style={{ color: '#666', display: 'block', marginTop: 12 }}>v{APP_VERSION}</Text>
      </div>
    );
  }

  if (!email) {
    return (
      <div style={{ padding: '20px' }}>
        <MessageBar intent="info">
          <MessageBarBody>No email selected. Please open an email to use Athena Outlook Assistant.</MessageBarBody>
        </MessageBar>
        <Text size={200} style={{ color: '#666', display: 'block', marginTop: 12 }}>v{APP_VERSION}</Text>
      </div>
    );
  }

  const toggles = preferences?.featureToggles ?? DEFAULT_FEATURE_TOGGLES;
  const visibleTabs = (Object.keys(TAB_FEATURE) as Array<keyof typeof TAB_FEATURE>).filter(
    (tab) =>
      tab === 'settings' ||
      (preferences
        ? tab === 'automation'
          ? (toggles.actions || toggles.automation)
          : toggles[TAB_FEATURE[tab] as keyof typeof toggles]
        : true)
  );
  const effectiveTab = visibleTabs.includes(selectedTab) ? selectedTab : (visibleTabs[0] ?? 'settings');

  return (
    <ErrorBoundary>
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TabList
        selectedValue={effectiveTab}
        onTabSelect={(_, data) => setSelectedTab(data.value as string)}
        style={{ borderBottom: '1px solid #e1dfdd' }}
      >
        {visibleTabs.includes('summary') && <Tab value="summary">Summary</Tab>}
        {visibleTabs.includes('links') && <Tab value="links">Links</Tab>}
        {visibleTabs.includes('automation') && <Tab value="automation">Automation</Tab>}
        <Tab value="settings">Settings</Tab>
      </TabList>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {effectiveTab === 'summary' && (
          <SummaryPanel
            email={email}
            summaryPreferences={
              preferences
                ? {
                    spoofDetection: preferences.featureToggles?.spoofDetection ?? true,
                    summaryLength: preferences.summaryLength ?? 'short',
                  }
                : undefined
            }
          />
        )}
        {effectiveTab === 'links' && <LinksPanel email={email} />}
        {effectiveTab === 'automation' && (
          <TasksPanel
            email={email}
            tasksEnabled={preferences?.featureToggles?.actions ?? true}
          />
        )}
        {effectiveTab === 'settings' && (
          <SettingsPanel onPreferencesSaved={handlePreferencesSaved} />
        )}
      </div>

      <footer style={{ padding: '8px 16px', borderTop: '1px solid #e1dfdd', flexShrink: 0 }}>
        <Text size={200} style={{ color: '#666' }}>Athena Outlook Assistant v{APP_VERSION}</Text>
      </footer>
    </div>
    </ErrorBoundary>
  );
};

export default App;
